import React, { useState, useEffect } from 'react';
import { getQuizWisePerformance } from '../apiClient';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface QuizData {
  quiz_id: number;
  quiz_reference: string;
  quiz_name: string;
  completion_date: string;
  subjects: string[];
  total_questions: number;
  answered: number;
  correct: number;
  incorrect: number;
  unanswered: number;
  score: number;
  percentage: number;
  time_spent_seconds: number;
  average_time_per_question: number;
  quiz_level: string;
  attempt_number: number;
}

interface QuizPerformanceData {
  user_id: number;
  total_quizzes_completed: number;
  quizzes: QuizData[];
}

const QuizPerformance: React.FC = () => {
  const [quizData, setQuizData] = useState<QuizPerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'score' | 'percentage'>('date');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showChart, setShowChart] = useState(false);
  const itemsPerPage = 5;

  useEffect(() => {
    loadQuizPerformanceData();
  }, []);

  const loadQuizPerformanceData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getQuizWisePerformance();
      if (response?.result) {
        setQuizData(response.result);
      } else {
        setError('No quiz performance data found');
      }
    } catch (error: any) {
      console.error('Failed to load quiz performance data:', error);
      setError(error.message || 'Failed to load quiz performance data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    if (seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPerformanceColor = (percentage: number): string => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  const getLevelBadgeColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'advance':
      case 'advanced':
        return 'danger';
      case 'intermediate':
        return 'warning';
      case 'elementary':
      default:
        return 'info';
    }
  };

  const sortedAndFilteredQuizzes = () => {
    if (!quizData?.quizzes) return [];

    let filtered = quizData.quizzes;

    // Apply level filter
    if (filterLevel !== 'all') {
      filtered = filtered.filter(quiz => 
        quiz.quiz_level.toLowerCase() === filterLevel.toLowerCase()
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.completion_date).getTime() - new Date(a.completion_date).getTime();
        case 'score':
          return b.score - a.score;
        case 'percentage':
          return b.percentage - a.percentage;
        default:
          return 0;
      }
    });

    return sorted;
  };

  const paginatedQuizzes = () => {
    const sorted = sortedAndFilteredQuizzes();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sorted.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(sortedAndFilteredQuizzes().length / itemsPerPage);

  // Prepare chart data
  const prepareChartData = () => {
    if (!quizData?.quizzes) return null;

    const sortedQuizzes = [...quizData.quizzes]
      .sort((a, b) => new Date(a.completion_date).getTime() - new Date(b.completion_date).getTime())
      .slice(-10); // Show last 10 quizzes for better readability

    const labels = sortedQuizzes.map((quiz, index) => 
      `Quiz ${index + 1}`
    );

    return {
      labels,
      datasets: [
        {
          label: 'Percentage Score',
          data: sortedQuizzes.map(quiz => quiz.percentage),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          yAxisID: 'y',
          tension: 0.3,
        },
        {
          label: 'Correct Answers',
          data: sortedQuizzes.map(quiz => quiz.correct),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          yAxisID: 'y1',
          tension: 0.3,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Quiz Performance Trends'
      },
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          afterLabel: function(context: any) {
            const quiz = quizData?.quizzes[context.dataIndex];
            if (quiz) {
              return [
                `Total Questions: ${quiz.total_questions}`,
                `Quiz Level: ${quiz.quiz_level}`,
                `Date: ${formatDate(quiz.completion_date)}`
              ];
            }
            return [];
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Quiz Progression'
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Percentage (%)'
        },
        min: 0,
        max: 100,
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Correct Answers'
        },
        grid: {
          drawOnChartArea: false,
        },
      }
    },
  };

  const chartData = prepareChartData();

  if (isLoading) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <h5 className="card-title mb-4">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Quiz Performance History
          </h5>
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading quiz performance data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <h5 className="card-title mb-4">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Quiz Performance History
          </h5>
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
          <button 
            className="btn btn-primary" 
            onClick={loadQuizPerformanceData}
          >
            <i className="fas fa-refresh me-2"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!quizData || quizData.quizzes.length === 0) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-body p-4">
          <h5 className="card-title mb-4">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Quiz Performance History
          </h5>
          <div className="text-center py-5">
            <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No quiz data available</h6>
            <p className="text-muted small">Complete some quizzes to see your performance history here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
      <div className="card-body p-4">
        {/* Header */}
        <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-start align-items-lg-center mb-4 gap-3">
          <h5 className="card-title mb-0">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Quiz Performance History
          </h5>
          <div className="d-flex flex-column flex-sm-row gap-2">
            <button 
              className={`btn btn-sm ${showChart ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setShowChart(!showChart)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <i className={`fas ${showChart ? 'fa-table' : 'fa-chart-line'} me-1`}></i>
              {showChart ? 'Show Table' : 'Show Chart'}
            </button>
            <span className="badge bg-primary rounded-pill align-self-center align-self-sm-start d-flex align-items-center justify-content-center"
                  style={{ minHeight: '32px', padding: '8px 12px' }}>
              {quizData.total_quizzes_completed} Quizzes Completed
            </span>
          </div>
        </div>

        {/* Performance Chart */}
        {showChart && chartData && (
          <div className="mb-4">
            <div className="card border-0 bg-light">
              <div className="card-body">
                <div style={{ width: '100%', height: '400px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
                <div className="text-center mt-3">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Showing performance trends for the last {Math.min(10, quizData.quizzes.length)} quizzes
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Sorting */}
        {!showChart && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label small text-muted">Sort by:</label>
              <select 
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'score' | 'percentage')}
              >
                <option value="date">Latest First</option>
                <option value="percentage">Best Performance</option>
                <option value="score">Highest Score</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted">Filter by Level:</label>
              <select 
                className="form-select form-select-sm"
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
              >
                <option value="all">All Levels</option>
                <option value="elementary">Elementary</option>
                <option value="intermediate">Intermediate</option>
                <option value="advance">Advanced</option>
              </select>
            </div>
          </div>
        )}

        {/* Quiz Table */}
        {!showChart && (
          <>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Quiz Details</th>
                    <th scope="col" className="text-center">Questions</th>
                    <th scope="col" className="text-center">Correct</th>
                    <th scope="col" className="text-center">Incorrect</th>
                    <th scope="col" className="text-center">Unanswered</th>
                    <th scope="col" className="text-center">Score</th>
                    <th scope="col" className="text-center">Percentage</th>
                    <th scope="col" className="text-center">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedQuizzes().map((quiz) => (
                    <tr key={quiz.quiz_id}>
                      {/* Quiz Details */}
                      <td style={{ minWidth: '250px' }}>
                        <div>
                          <h6 className="mb-1 fw-bold text-truncate" title={quiz.quiz_name}>
                            {quiz.quiz_name.length > 30 ? quiz.quiz_name.substring(0, 30) + '...' : quiz.quiz_name}
                          </h6>
                          <p className="text-muted small mb-2">
                            <i className="fas fa-calendar me-1"></i>
                            {formatDate(quiz.completion_date)}
                          </p>
                          <div className="d-flex flex-wrap gap-1 mb-1">
                            {quiz.subjects.map((subject, index) => (
                              <span 
                                key={index}
                                className="badge bg-light text-dark"
                                style={{ fontSize: '0.65rem' }}
                              >
                                {subject}
                              </span>
                            ))}
                          </div>
                          <span 
                            className={`badge bg-${getLevelBadgeColor(quiz.quiz_level)}`}
                            style={{ fontSize: '0.65rem' }}
                          >
                            {quiz.quiz_level}
                          </span>
                        </div>
                      </td>

                      {/* Questions */}
                      <td className="text-center">
                        <strong>{quiz.total_questions}</strong>
                      </td>

                      {/* Correct */}
                      <td className="text-center">
                        <span className="text-success fw-bold">{quiz.correct}</span>
                      </td>

                      {/* Incorrect */}
                      <td className="text-center">
                        <span className="text-danger fw-bold">{quiz.incorrect}</span>
                      </td>

                      {/* Unanswered */}
                      <td className="text-center">
                        <span className="text-warning fw-bold">{quiz.unanswered}</span>
                      </td>

                      {/* Score */}
                      <td className="text-center">
                        <span className={`fw-bold ${quiz.score >= 0 ? 'text-success' : 'text-danger'}`}>
                          {quiz.score}
                        </span>
                      </td>

                      {/* Percentage */}
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center">
                          <span 
                            className={`badge bg-${getPerformanceColor(quiz.percentage)} mb-1`}
                            style={{ fontSize: '0.75rem', minWidth: '50px' }}
                          >
                            {quiz.percentage.toFixed(1)}%
                          </span>
                          <div className="progress" style={{ height: '4px', width: '60px' }}>
                            <div 
                              className={`progress-bar bg-${getPerformanceColor(quiz.percentage)}`}
                              style={{ width: `${quiz.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>

                      {/* Time */}
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center">
                          <span className="fw-bold text-info mb-1">
                            <i className="fas fa-clock me-1"></i>
                            {formatTime(quiz.time_spent_seconds)}
                          </span>
                          <small className="text-muted">
                            {quiz.average_time_per_question.toFixed(1)}s/q
                          </small>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          
        </>
        )}

        {/* Pagination */}
        {!showChart && totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <nav>
              <ul className="pagination pagination-sm">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li 
                    key={index + 1} 
                    className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                  >
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        {/* Summary Stats */}
        <div className="row mt-4 pt-3 border-top">
          <div className="col-md-3 text-center">
            <small className="text-muted d-block">Best Performance</small>
            <strong className="text-success">
              {Math.max(...quizData.quizzes.map(q => q.percentage)).toFixed(1)}%
            </strong>
          </div>
          <div className="col-md-3 text-center">
            <small className="text-muted d-block">Average Score</small>
            <strong>
              {(quizData.quizzes.reduce((sum, q) => sum + q.percentage, 0) / quizData.quizzes.length).toFixed(1)}%
            </strong>
          </div>
          <div className="col-md-3 text-center">
            <small className="text-muted d-block">Total Time</small>
            <strong className="text-info">
              {formatTime(quizData.quizzes.reduce((sum, q) => sum + q.time_spent_seconds, 0))}
            </strong>
          </div>
          <div className="col-md-3 text-center">
            <small className="text-muted d-block">Questions Answered</small>
            <strong className="text-primary">
              {quizData.quizzes.reduce((sum, q) => sum + q.answered, 0)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPerformance;
